// Copyright ©️ 2026 eVolve MEP, LLC

import { type TaskId, type WorkCell, useEvolvePaginatedGet, type Facility } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { isNotNil } from 'helpers/isNotNil';
import { CreateWorkCellModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/CreateWorkCellModal';

type PartialWorkCell = Pick<WorkCell, 'workCellName' | 'workCellId'>;

export type WorkCellSelectProps = {
  value?: PartialWorkCell | null;
  onChange: (value: PartialWorkCell | null) => void;
  taskIds?: TaskId[];
  label?: string;
  disabled?: boolean;
  /** If provided, will allow the user to create a new Work Cell in this facility */
  facility?: Pick<Facility, 'facilityId' | 'facilityName'>;
};

export const WorkCellSelect = ({ value, onChange, taskIds, label, facility, ...props }: WorkCellSelectProps) => {
  const paginatedGet = useEvolvePaginatedGet(`shop/workCell/viableWorkCells`, {
    defaultConfig: {
      params: {
        taskIds,
      },
    },
  });
  const [selectedWorkCell, setSelectedWorkCell] = useUncontrolled({
    value,
    onChange,
  });

  return (
    <SearchableSelect
      label={label}
      placeholder="Select work cell"
      idKey="workCellId"
      nothingFoundMessage="No work cells found."
      getItemLabel={(wc) => wc.workCellName}
      paginatedGet={paginatedGet}
      value={selectedWorkCell}
      onChange={(v) => {
        if (v) setSelectedWorkCell(v);
      }}
      creatable={isNotNil(facility)}
      createLabel="Create new work cell"
      creationModal={(workCellName, { opened, onClose }) =>
        isNotNil(facility) && (
          <CreateWorkCellModal
            defaultName={workCellName}
            opened={opened}
            onClose={onClose}
            onWorkCellCreated={setSelectedWorkCell}
            facility={facility}
          />
        )
      }
      preloadData
      useLike={false}
      searchKey="workCellName"
      {...props}
    />
  );
};
